import { Box, Typography, useTheme, Menu, MenuItem } from "@mui/material";
import Assets from "../../Component/Common/Assets";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import { getVandorOverviewAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";
import dayjs from "dayjs";

// --------------------- Circular Progress ---------------------
const CircularProgress = ({ percentage, color, size = 140 }) => {
  const theme = useTheme();
  const backgroundStrokeWidth = 8;
  const progressStrokeWidth = 14;
  const radius = (size - progressStrokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#f5f5f5"
          strokeWidth={backgroundStrokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={progressStrokeWidth}
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: theme.palette.bgDarkGray.neutral,
            mb: 0.5,
          }}
        >
          Vendors
        </Typography>
        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 900,
            color: theme.palette.bgBlack.neutral100,
            lineHeight: 1,
          }}
        >
          {percentage}%
        </Typography>
      </Box>
    </Box>
  );
};

// --------------------- Main Component ---------------------
function VenderOverView() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const setFullPageLoader = usePageLoader();
  const { setSnackBar } = useSnackBar();

  // --------------------- State ---------------------
  const currentDate = dayjs();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.month());

  const generateFinancialYears = (count) => {
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month();
    const financialYearStartMonth = 3; // April
    const currentFinancialYearStart =
      currentMonth >= financialYearStartMonth ? currentYear : currentYear - 1;

    return Array.from({ length: count }, (_, i) => {
      const startYear = currentFinancialYearStart - i;
      return `${startYear}-${(startYear + 1).toString().slice(-2)}`;
    });
  };

  const financialYears = generateFinancialYears(10);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYears[0]
  );

  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [yearMenuAnchor, setYearMenuAnchor] = useState(null);
  const [vandorData, setVandorData] = useState([]);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // --------------------- Handlers ---------------------
  const getDateRange = () => {
    const startYear = parseInt(selectedFinancialYear.split("-")[0]);
    const endYear = startYear + 1;
    const financialYearStartMonth = 3;

    let startDate, endDate;
    if (selectedMonth >= financialYearStartMonth) {
      startDate = dayjs().year(startYear).month(selectedMonth).startOf("month");
      endDate = dayjs().year(startYear).month(selectedMonth).endOf("month");
    } else {
      startDate = dayjs().year(endYear).month(selectedMonth).startOf("month");
      endDate = dayjs().year(endYear).month(selectedMonth).endOf("month");
    }

    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  };

 const getVenderOverviewData = async () => {
  setFullPageLoader(true);
  const { startDate, endDate } = getDateRange();
  try {
    const res = await dispatch(getVandorOverviewAction({ startDate, endDate }));
    const ok = ErrorHandler(res, setSnackBar);

    if (ok) {
      setVandorData(res.payload.data.response);
    }
  } catch (err) {
  } finally {
    setFullPageLoader(false);
  }
};


  const handleMonthClick = (event) => setMonthMenuAnchor(event.currentTarget);
  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setMonthMenuAnchor(null);
  };

  const handleYearClick = (event) => setYearMenuAnchor(event.currentTarget);
  const handleFinancialYearSelect = (financialYear) => {
    setSelectedFinancialYear(financialYear);
    setYearMenuAnchor(null);
  };

  // --------------------- Effects ---------------------
  useEffect(() => {
    getVenderOverviewData();
  }, [selectedMonth, selectedFinancialYear]);

  return (
    <Box
      sx={{
        height: { xs: "auto", md: "352px" },
        border: `0.5px solid ${theme.palette.bgWhite.secondary}`,
        background: theme.palette.bgWhite.third,
        borderRadius: "20px",
        padding: "20px 20px",
        boxSizing: "border-box",
        overflow: "auto",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            color: theme.palette.bgBlack.secondary,
          }}
        >
          Vendor Overview
        </Typography>

        {/* Month & Year Selectors */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          {/* Month Selector */}
          <Box
            onClick={handleMonthClick}
            sx={{
              display: "flex",
              padding: "6px 16px",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              borderRadius: "30px",
              border: `1px solid ${theme.palette.bgWhite.secondary}`,
              boxShadow:
                "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
              cursor: "pointer",
            }}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: theme.palette.bgDarkGray.grey600,
              }}
            >
              {months[selectedMonth]}
            </Typography>
          </Box>
          <Menu
            anchorEl={monthMenuAnchor}
            open={Boolean(monthMenuAnchor)}
            onClose={() => setMonthMenuAnchor(null)}
          >
            {months.map((month, index) => (
              <MenuItem key={month} onClick={() => handleMonthSelect(index)}>
                {month}
              </MenuItem>
            ))}
          </Menu>

          {/* Year Selector */}
          <Box
            onClick={handleYearClick}
            sx={{
              display: "flex",
              padding: "6px 16px",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              borderRadius: "30px",
              border: `1px solid ${theme.palette.bgWhite.secondary}`,
              boxShadow:
                "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
              cursor: "pointer",
            }}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: theme.palette.bgDarkGray.grey600,
              }}
            >
              FY {selectedFinancialYear}
            </Typography>
          </Box>
          <Menu
            anchorEl={yearMenuAnchor}
            open={Boolean(yearMenuAnchor)}
            onClose={() => setYearMenuAnchor(null)}
          >
            {financialYears.map((year) => (
              <MenuItem
                key={year}
                onClick={() => handleFinancialYearSelect(year)}
              >
                FY {year}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Circular Progress Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: 3,
          minHeight: "180px",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.bgDarkGray.main,
              mb: 2.5,
            }}
          >
            Registered
          </Typography>
          <CircularProgress
            percentage={vandorData?.[0]?.percentage}
            color={theme.palette.lightTomato.secondary}
            size={150}
          />
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.palette.bgDarkGray.main,
              mb: 2.5,
            }}
          >
            Non-registered
          </Typography>
          <CircularProgress
            percentage={vandorData?.[1]?.percentage}
            color={theme.palette.lightTomato.secondary}
            size={150}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default VenderOverView;
